import { useEffect, useState } from 'react';
import {
  AlertTriangle, Database, Download, Loader2, MessageSquare,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';

import { exportApi, type ExportKb, type ExportSummary } from '@/api/export';

/**
 * 資料匯出頁（docs/EXPORT_FEATURE_PLAN.md）
 *
 * - 對話紀錄：所有登入角色皆可匯出自己的（含 org_viewer，2026-09-15 客戶需求）；
 *   知識庫原始檔：只有 super_admin / org_user，其餘角色整個區塊隱藏
 *   （依 summary.kb_export_allowed，權限判斷在後端）。
 *   單機版由 router 的 HideInSingleUserMode 整頁隱藏。
 * - 下載一律 window.location.assign() 交給瀏覽器：同源 cookie 自動帶上、
 *   有原生進度、大檔不佔記憶體。可預期的錯誤（沒選、可下載 0、超過上限）
 *   全部在按下前以 summary 資料擋掉。
 */

function formatBytes(n: number): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** 觸發瀏覽器下載；按鈕鎖 3 秒防連點 */
function triggerDownload(url: string, setBusy: (b: boolean) => void) {
  setBusy(true);
  window.location.assign(url);
  window.setTimeout(() => setBusy(false), 3000);
}

export default function ExportPage() {
  const [summary, setSummary] = useState<ExportSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [kbBusy, setKbBusy] = useState(false);
  const [convBusy, setConvBusy] = useState(false);

  useEffect(() => {
    exportApi
      .summary()
      .then(setSummary)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const kbs: ExportKb[] = summary?.kbs ?? [];
  const downloadableKbs = kbs.filter((k) => k.downloadable_count > 0);
  const selectedKbs = kbs.filter((k) => selected.has(k.id) && k.downloadable_count > 0);
  const selectedBytes = selectedKbs.reduce((acc, k) => acc + k.downloadable_bytes, 0);
  const allSelected =
    downloadableKbs.length > 0 && downloadableKbs.every((k) => selected.has(k.id));
  const maxZip = summary?.max_zip_bytes ?? 0;
  const overLimit = maxZip > 0 && selectedBytes > maxZip;
  const convCount = summary?.conversations.count ?? 0;

  const toggleOne = (id: string, on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (on: boolean) => {
    setSelected(on ? new Set(downloadableKbs.map((k) => k.id)) : new Set());
  };

  const downloadKbs = (ids: string[]) => {
    if (ids.length === 0) return;
    triggerDownload(exportApi.kbZipUrl(ids), setKbBusy);
  };

  return (
    <div className="p-6 space-y-6">
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <Loader2 className="size-5 animate-spin" />
          <span>載入中…</span>
        </div>
      )}

      {!loading && error && (
        <div className="text-center py-8 text-red-500 text-sm">{error}</div>
      )}

      {!loading && !error && summary && (
        <>
          {/* ── 知識庫原始檔（僅 super_admin / org_user；其餘角色整塊隱藏）──── */}
          {summary.kb_export_allowed && (
          <section className="bg-card border border-border rounded-lg p-4 shadow-sm space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Database className="size-5 text-primary" />
                  <h3 className="font-semibold text-foreground">知識庫原始檔</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  下載您可見知識庫中上傳的原始檔案（PDF、DOCX…）。不含系統產生的索引資料。
                </p>
              </div>
              <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                已選 {selectedKbs.length} 個 · {formatBytes(selectedBytes)}
                {maxZip > 0 && <div>單次上限 {formatBytes(maxZip)}</div>}
              </div>
            </div>

            {kbs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                沒有可見的知識庫
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        disabled={downloadableKbs.length === 0}
                        onCheckedChange={(v) => toggleAll(v === true)}
                        aria-label="全選"
                      />
                    </TableHead>
                    <TableHead>知識庫</TableHead>
                    <TableHead className="w-20 text-right">文件</TableHead>
                    <TableHead className="w-24 text-right">可下載</TableHead>
                    <TableHead className="w-24 text-right">大小</TableHead>
                    <TableHead className="w-14 text-right">下載</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kbs.map((kb) => {
                    const none = kb.downloadable_count === 0;
                    return (
                      <TableRow key={kb.id} className={none ? 'opacity-60' : undefined}>
                        <TableCell>
                          <Checkbox
                            checked={selected.has(kb.id)}
                            disabled={none}
                            onCheckedChange={(v) => toggleOne(kb.id, v === true)}
                            aria-label={`選取 ${kb.name}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-foreground truncate max-w-md">{kb.name}</div>
                          {none && (
                            <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                              <AlertTriangle className="size-3.5" />
                              此知識庫的文件上傳於保留功能上線前，沒有原始檔可下載
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground text-sm">
                          {kb.file_count}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground text-sm">
                          {kb.downloadable_count}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground text-sm">
                          {formatBytes(kb.downloadable_bytes)}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title={none ? '無原始檔' : '下載此知識庫原始檔（zip）'}
                              disabled={none || kbBusy || kb.downloadable_bytes > maxZip}
                              onClick={() => downloadKbs([kb.id])}
                            >
                              <Download className="size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            <div className="flex items-center justify-end gap-3">
              {overLimit && (
                <span className="text-xs text-red-500">
                  所選檔案總量超過單次上限，請減少選取
                </span>
              )}
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
                disabled={selectedKbs.length === 0 || overLimit || kbBusy}
                onClick={() => downloadKbs(selectedKbs.map((k) => k.id))}
              >
                {kbBusy ? <Loader2 className="size-4 mr-1 animate-spin" /> : <Download className="size-4 mr-1" />}
                下載所選知識庫原始檔（zip）
              </Button>
            </div>
          </section>
          )}

          {/* ── 對話紀錄 ─────────────────────────────────────────────── */}
          <section className="bg-card border border-border rounded-lg p-4 shadow-sm space-y-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <MessageSquare className="size-5 text-primary" />
                <h3 className="font-semibold text-foreground">對話紀錄</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                匯出您帳號下的全部對話。目前共 {convCount} 個對話、
                {summary.conversations.message_count} 則訊息。
              </p>
              <p className="text-xs text-muted-foreground">
                zip 內容：對話紀錄.xlsx（Excel 總表，一列一則訊息、圖片已內嵌）、
                conversations.json（結構化資料）、回答引用的圖片與對話中上傳的附件。
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
                disabled={convCount === 0 || convBusy}
                onClick={() => triggerDownload(exportApi.conversationsUrl(), setConvBusy)}
              >
                {convBusy ? <Loader2 className="size-4 mr-1 animate-spin" /> : <Download className="size-4 mr-1" />}
                下載全部對話紀錄（zip）
              </Button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
